import { useState } from "react";
import { useRouter } from "next/router";
import { stringify } from "qs";

import { clearKeysFromQuery } from "hooks/useClearQueryStringKeys";
import { useSelector } from "redux/hooks";
import { getCountryCode, getIndicatedAddress } from "redux/selectors/location";
import parseNextRouterAsPath from "utils/parseNextRouterAsPath";

import MetaTags from "components/MetaTags";
import ShopNoResults from "components/ShopNoResults";
import { ShopProps } from "pages/shop";

import AppliedChips from "./AppliedChips";
import CitiesNearby from "./CitiesNearby/CitiesNearby";
import DesktopFilterColumnUI from "./DesktopFilterColumnUI";
import DynamicFilters from "./DynamicFilters";
import FulfillmentFilters from "./FulfillmentFilters";
import Header from "./Header";
import { createHeaderString } from "./Header/Header";
import MobileFilterModalAndShortcutsUI from "./MobileFilterModalAndShortcutsUI";
import ShopItemResults from "./ShopItemResults";
import { extractFilterValues } from "./utils/filterUtils";

export const createMetaDescriptionString = (
  selectedCategories: string[] | null,
  selectedFulfillments: string[] | null,
  indicatedAddress: string | null,
) => {
  const categoriesString = selectedCategories?.length
    ? `the best ${selectedCategories.join(" and ").toLowerCase()}`
    : "cannabis products";

  const fulfillmentsString = selectedFulfillments?.length
    ? `${selectedCategories?.length ? " available" : ""} for ${selectedFulfillments.join(" or ").toLowerCase()}`
    : "";

  return `Shop ${categoriesString}${fulfillmentsString}${indicatedAddress ? ` near you in ${indicatedAddress}` : ""}`;
};

export type UnifiedShopProps = Pick<
  ShopProps,
  "shopItems" | "availableFilters" | "totalCount" | "citiesNearby"
>;

export const UNIFIED_SHOP_PAGE_SIZE = 15;

const UnifiedShop: React.FC<
  UnifiedShopProps & { indexShopPageEnabled: boolean }
> = ({
  shopItems,
  totalCount,
  availableFilters,
  citiesNearby,
  indexShopPageEnabled,
}: UnifiedShopProps & { indexShopPageEnabled: boolean }) => {
  const { asPath } = useRouter();
  const indicatedAddress = useSelector(getIndicatedAddress);
  const { path, query: parsedQs } = parseNextRouterAsPath(asPath);
  const [openFilterSection, setOpenFilterSection] = useState<string>();
  const countryCode = useSelector(getCountryCode);
  const isUsCountryCode = countryCode === "US";

  const { selectedCategories, selectedFulfillments } =
    extractFilterValues(parsedQs);

  const headerString = createHeaderString(
    selectedCategories,
    selectedFulfillments,
    indicatedAddress,
  );
  const description = createMetaDescriptionString(
    selectedCategories,
    selectedFulfillments,
    indicatedAddress,
  );

  return (
    <>
      <MetaTags
        canonicalParams={["location", "fulfillment[]", "filter[category]"]}
        description={description}
        preserveParamOrder={indexShopPageEnabled}
        robots={indexShopPageEnabled ? "all" : "noindex"}
        title={headerString}
      />

      <Header />

      <section className="container" data-testid="filter-layout">
        <div className="grid grid-cols-1 gap-1 md:gap-8 md:grid-cols-4 md:m-0">
          <div>
            <DesktopFilterColumnUI
              chips={<AppliedChips availableFilters={availableFilters} />}
              filters={
                <>
                  <FulfillmentFilters />
                  <DynamicFilters
                    availableFilters={availableFilters}
                    defaultOpenKey={openFilterSection}
                  />
                </>
              }
            />
            <MobileFilterModalAndShortcutsUI
              availableFilters={availableFilters}
              setOpenFilterSection={setOpenFilterSection}
              filters={
                <>
                  <FulfillmentFilters sectionPadding />
                  <DynamicFilters
                    availableFilters={availableFilters}
                    defaultOpenKey={openFilterSection}
                    sectionPadding
                  />
                </>
              }
              chips={<AppliedChips availableFilters={availableFilters} />}
            />
          </div>

          <div className="md:col-span-3">
            {shopItems.length > 0 ? (
              <ShopItemResults shopItems={shopItems} totalCount={totalCount} />
            ) : (
              <ShopNoResults
                titleText="No matching results"
                subtitleText="Try selecting fewer options or reset all filters"
                wrapperClassName="my-md"
                primaryLinkText="reset filters"
                action={{
                  pathname: path,
                  query: stringify(
                    clearKeysFromQuery(parsedQs, [
                      "filter",
                      "fulfillment",
                      "q",
                    ]),
                    {
                      arrayFormat: "brackets",
                      indices: false,
                    },
                  ),
                }}
              />
            )}
          </div>
        </div>
      </section>
      {isUsCountryCode && (
        <div className="bg-leafly-white">
          <CitiesNearby cities={citiesNearby} />
        </div>
      )}
    </>
  );
};

export default UnifiedShop;
